import { useState } from "react";
import { PhoneCall, Plus, Trash2, Ambulance } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { customerApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

export default function EmergencyContacts() {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.customerProfile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const initial = profile?.emergencyContacts || profile?.emergency_contacts || [];
  const [contacts, setContacts] = useState(initial);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relation, setRelation] = useState("");
  const [saving, setSaving] = useState(false);

  const persist = async (next) => {
    setSaving(true);
    try {
      await customerApi.updateProfile(user._id, { emergencyContacts: next });
      setProfile({ ...profile, emergencyContacts: next });
      setContacts(next);
    } catch (e) {
      toast.error("Could not save. Please try again.");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!name.trim() || phone.replace(/\D/g, "").length !== 10) return toast.error("Enter a name and 10-digit phone");
    try {
      await persist([...contacts, { name: name.trim(), phone: phone.replace(/\D/g, ""), relation: relation.trim() || "Contact" }]);
      setName(""); setPhone(""); setRelation("");
      toast.success("Contact added");
    } catch (e) { /* handled */ }
  };

  const remove = async (idx) => {
    try {
      await persist(contacts.filter((_, i) => i !== idx));
      toast.success("Contact removed");
    } catch (e) { /* handled */ }
  };

  return (
    <div className="max-w-3xl" data-testid="emergency-contacts-page">
      <PageHeader title="Emergency Contacts" subtitle="People we'll alert in an emergency" icon={PhoneCall} color="#DC2626" />

      <a href="tel:112" className="flex items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 mb-5" data-testid="ec-call-112">
        <div className="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center"><Ambulance className="h-5 w-5 text-destructive" /></div>
        <div className="flex-1"><p className="font-semibold text-destructive text-sm">National Emergency 112</p><p className="text-xs text-muted-foreground">Tap to call now</p></div>
      </a>

      <div className="space-y-2 mb-5">
        {contacts.length === 0 && <p className="text-sm text-muted-foreground">No emergency contacts yet. Add one below.</p>}
        {contacts.map((c, i) => (
          <Card key={i} className="rounded-2xl p-3.5 border-border flex items-center gap-3" data-testid={`ec-item-${i}`}>
            <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center font-bold text-secondary-foreground">{(c.name || "?").charAt(0).toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{c.name}</p>
              <p className="text-sm text-muted-foreground">{c.relation} · +91 {c.phone}</p>
            </div>
            <a href={`tel:${c.phone}`} className="h-9 w-9 rounded-xl bg-[#059669]/10 flex items-center justify-center" data-testid={`ec-call-${i}`}><PhoneCall className="h-4 w-4 text-[#059669]" /></a>
            <button onClick={() => remove(i)} disabled={saving} className="h-9 w-9 rounded-xl bg-destructive/10 flex items-center justify-center" data-testid={`ec-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
          </Card>
        ))}
      </div>

      <Card className="rounded-3xl p-5 border-border">
        <p className="font-semibold mb-3">Add a contact</p>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-2xl h-11" data-testid="ec-name" />
          <Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} maxLength={10} placeholder="10-digit phone" className="rounded-2xl h-11" data-testid="ec-phone" />
          <Input value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="Relation (e.g. Spouse)" className="rounded-2xl h-11" data-testid="ec-relation" />
          <Button onClick={add} disabled={saving} className="w-full h-12 rounded-2xl bg-[#059669]" data-testid="ec-add"><Plus className="h-4 w-4 mr-1.5" /> Add Contact</Button>
        </div>
      </Card>
    </div>
  );
}
